import { useEffect, useState } from 'react';
import styles from './Pagination.module.css';
import { useLanguage } from '../../../context/LanguageContext';

interface PaginationProps {
  totalItems: number;
  itemsPerPage?: number;
  currentPage?: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

type PageNumber = number | '...';

export default function Pagination({ 
  totalItems, 
  itemsPerPage = 10, 
  currentPage = 1, 
  onPageChange,
  onItemsPerPageChange 
}: PaginationProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const { t } = useLanguage();

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPageChange(totalPages);
    }
  }, [totalPages, currentPage, onPageChange]);

  const goToPage = (pageNumber: number) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const getPageNumbers = (): PageNumber[] => {
    const pages: PageNumber[] = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleItemsPerPageChange = (value: number) => {

    localStorage.setItem('perPage', value.toString());

    onItemsPerPageChange(value);
    onPageChange(1);
    setIsDropdownOpen(false);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const rowsPerPageOptions = [5, 10, 25, 50, 100];

  if (totalItems === 0) {
    return null;
  }

  return (
    <div className={styles.pagination}>
      <div className={styles.paginationInfo}>
        {t("showing")} {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, totalItems)} {t("of")} {totalItems} {t("entries")}
      </div>
      
      <div className={styles.paginationControls}>
        <button 
          className={styles.paginationButton}
          onClick={goToPrevPage}
          disabled={currentPage === 1}
          aria-label={t("previousPage")}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 12L6 8L10 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={`ellipsis-${index}`} className={styles.paginationEllipsis}>
              ...
            </span>
          ) : (
            <button
              key={page}
              className={`${styles.paginationButton} ${currentPage === page ? styles.active : ''}`}
              onClick={() => goToPage(page as number)}
              aria-label={`${t("goToPage")} ${page}`}
              aria-current={currentPage === page ? 'page' : undefined}
            >
              {page}
            </button>
          )
        ))}
        
        <button 
          className={styles.paginationButton}
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
          aria-label={t("nextPage")}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 4L10 8L6 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.rowsPerPageSelector}>
        {/* <label htmlFor="rows-per-page">{t("rowsPerPage")}:</label> */}
        <div className={styles.customDropdown}>
          <button
            className={styles.dropdownButton}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            <span>{itemsPerPage}</span>
            <span className={`${styles.arrow} ${isDropdownOpen ? styles.arrowOpen : ''}`}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>
          
          {isDropdownOpen && (
            <div className={styles.dropdownMenu} role="listbox">
              {rowsPerPageOptions.map((option) => (
                <div
                  key={option}
                  className={`${styles.dropdownItem} ${option === itemsPerPage ? styles.selected : ''}`}
                  onClick={() => handleItemsPerPageChange(option)}
                  role="option"
                  aria-selected={option === itemsPerPage}
                >
                  <span>{option}</span>
                  {option === itemsPerPage && (
                    <span className={styles.checkmark}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.5 4L6 11.5L2.5 8"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
